import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Api } from "../lib/api.js";

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
            ux_mode?: "popup" | "redirect";
            auto_select?: boolean;
          }) => void;
          renderButton: (el: HTMLElement, options: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

/**
 * Phone-side claim page. The TV showed a QR pointing here with ?code=ABC123.
 * We load the Google Identity Services library, show their Sign-In button,
 * and on success POST the ID token to the server which:
 *   1. Verifies it with Google's keys
 *   2. Finds or creates a wallace-tv user keyed on the Google sub
 *   3. Approves the pairing code, attaching a session token
 *
 * The TV is polling /api/auth/pairing/:code/status so as soon as we return,
 * it picks up the token and replaces its page with the signed-in home view.
 */
export function ClaimGoogle(): JSX.Element {
  const [params] = useSearchParams();
  const code = params.get("code") ?? "";
  const [phase, setPhase] = useState<
    | { kind: "loading" }
    | { kind: "no-code" }
    | { kind: "no-config" }
    | { kind: "ready"; clientId: string }
    | { kind: "claiming" }
    | { kind: "done"; displayName: string | null; created: boolean }
    | { kind: "error"; message: string }
  >({ kind: "loading" });
  const btnRef = useRef<HTMLDivElement | null>(null);
  const initialized = useRef(false);

  // Step 1: pull the public client id from the backend.
  useEffect(() => {
    if (!code) { setPhase({ kind: "no-code" }); return; }
    Api.googleConfig().then((c) => {
      if (!c.client_id) { setPhase({ kind: "no-config" }); return; }
      setPhase({ kind: "ready", clientId: c.client_id });
    }).catch((err) => setPhase({ kind: "error", message: String(err) }));
  }, [code]);

  // Step 2: once we have a client id, inject the GIS script + render the button.
  useEffect(() => {
    if (phase.kind !== "ready") return;
    const clientId = phase.clientId;
    if (initialized.current) return;
    initialized.current = true;
    const existing = document.querySelector<HTMLScriptElement>("script[data-gis]");
    function init(): void {
      const gis = window.google?.accounts?.id;
      if (!gis || !btnRef.current) return;
      gis.initialize({
        client_id: clientId,
        callback: async (resp) => {
          setPhase({ kind: "claiming" });
          try {
            // No device_label here — server captures the user-agent itself
            // and the default "Android TV" label is the right fit for the
            // device this code was scanned from.
            const r = await Api.pairing.googleClaim(code, resp.credential);
            setPhase({ kind: "done", displayName: r.user?.display_name ?? null, created: r.user?.created ?? false });
          } catch (err) {
            setPhase({ kind: "error", message: (err as Error).message });
          }
        },
        ux_mode: "popup",
      });
      gis.renderButton(btnRef.current, {
        type: "standard",
        theme: "filled_blue",
        size: "large",
        text: "signin_with",
        shape: "pill",
        logo_alignment: "left",
      });
    }
    if (existing) {
      init();
    } else {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.defer = true;
      s.dataset.gis = "1";
      s.onload = init;
      document.head.appendChild(s);
    }
  }, [phase, code]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-lacquer-deep">
      <div className="card max-w-md w-full p-8 text-center">
        <div className="section-label text-gold">— PAIR DEVICE —</div>
        <h1 className="hero-title mt-3 text-3xl">Sign in to claim</h1>
        <div className="hero-underline mt-4 mx-auto w-48" />

        {code && (
          <div className="font-display text-goldHi text-2xl tracking-[0.4em] tabular-nums mt-6">
            {code}
          </div>
        )}
        <div className="text-parchment/70 text-xs mt-1">
          Make sure this code matches the one on your TV.
        </div>

        <div className="mt-8 min-h-[60px] flex items-center justify-center">
          {phase.kind === "loading" && <div className="skeleton h-10 w-48 rounded-full" />}
          {phase.kind === "no-code" && (
            <div className="text-vermilionHi text-sm">
              No pairing code in the URL — go back to the TV and scan again.
            </div>
          )}
          {phase.kind === "no-config" && (
            <div className="text-vermilionHi text-sm">
              Google sign-in isn't configured on this server.
            </div>
          )}
          {phase.kind === "ready" && (
            <div ref={btnRef} />
          )}
          {phase.kind === "claiming" && (
            <div className="text-parchment text-sm">Pairing…</div>
          )}
          {phase.kind === "done" && (
            <div>
              <div className="text-jade font-display text-xl">Paired.</div>
              <div className="text-parchment mt-2 text-sm">
                {phase.created ? "Account created." : "Welcome back."}
                {phase.displayName && <> Signed in as <span className="text-gold">{phase.displayName}</span>.</>}
              </div>
              <div className="text-parchment/70 text-xs mt-4">
                Look at your TV — it should already be loading.
              </div>
            </div>
          )}
          {phase.kind === "error" && (
            <div className="text-vermilionHi text-sm">{phase.message}</div>
          )}
        </div>
      </div>
    </div>
  );
}
